// Workaround for the React + Chrome Translate "removeChild on Node" crash.
//
// Chrome's built-in page translator (and similar extensions like the
// Microsoft Edge translator and the Google Translate browser extension)
// works by replacing text nodes in the live DOM with translated text nodes.
// React keeps its own pointer to the original text nodes via its fiber tree
// and, on unmount, calls Node.prototype.removeChild(originalTextNode) — but
// the translator has already swapped that node out, so it's no longer a
// child of the React-tracked parent and the browser throws:
//
//   NotFoundError: Failed to execute 'removeChild' on 'Node': The node to
//                  be removed is not a child of this node.
//
// The same conflict can happen on Node.prototype.insertBefore for inserts
// next to a node the translator has moved.
//
// The fix below is the canonical community workaround: make those two
// methods lenient. If the target node isn't actually parented to `this`
// anymore (because someone — usually a translator — moved or replaced it),
// don't throw. Just log it and skip the operation. React will recover on
// the next reconciliation pass because the next commit references fresh
// fibers; the missed remove leaves a translated leftover that the next
// render will overwrite.
//
// Trade-off: this could in theory swallow a real bug where we ask the DOM
// to remove the wrong node ourselves. In practice that pattern is
// vanishingly rare in modern React (the renderer owns the DOM) and the
// observable user impact of THIS crash — a full page white-screen with no
// way to recover except a hard reload — is much worse than a stale
// translated text node hanging around. We forward the warning to the
// console so it's still discoverable during development.
//
// References:
//   - https://github.com/facebook/react/issues/11538
//   - https://github.com/facebook/react/issues/17256
//
// Install once, at the very top of the entry module, before any React code
// runs. Safe to import multiple times; the install is idempotent via a flag.

let installed = false;

export function installChromeTranslateGuard() {
  if (installed) return;
  if (typeof Node !== 'function' || !Node.prototype) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function patchedRemoveChild(child) {
    if (child && child.parentNode !== this) {
      if (typeof console !== 'undefined' && console.warn) {
        // Tag the message so it's easy to find when triaging — anyone
        // searching for this string will land on this file.
        console.warn(
          '[chromeTranslateGuard] removeChild called on a node whose parent ' +
            'changed (likely Chrome Translate). Skipping.',
          { child, expectedParent: this, actualParent: child.parentNode },
        );
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function patchedInsertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn(
          '[chromeTranslateGuard] insertBefore called with a reference node ' +
            'whose parent changed (likely Chrome Translate). Appending instead.',
          {
            newNode,
            referenceNode,
            expectedParent: this,
            actualParent: referenceNode.parentNode,
          },
        );
      }
      // Append at the end of this node — better than dropping the insert
      // entirely, which can wedge React's fiber tree.
      return originalRemoveChild === Node.prototype.removeChild
        ? this.appendChild(newNode)
        : Node.prototype.appendChild.call(this, newNode);
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

export default installChromeTranslateGuard;
